import { THICKNESS_POLL_MS, THICKNESS_STALE_RUN_MS } from '@constants/cad';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createThicknessAnalysis,
  downloadThicknessPdf,
  downloadThicknessReport,
  getModelGlbPrefix,
  getThicknessAnalysis,
  listThicknessAnalyses,
  recoverThicknessAnalysis,
} from '../../../services/cad.service';
import { evictGlbsUnder } from '../../../services/cadGlbCache';
import {
  CADModel,
  ThicknessAnalysis,
  ThicknessRegion,
} from '../../../types/cad';
import { thicknessConditionsSchema } from '@/features/cad/schemas/thicknessConditions.schema';
import { apiErrorStatus, resolveApiErrorMessage } from '../../../utils/apiErrorMessages';
import { getRateLimitRetryAfterSeconds } from '../../../utils/errorMessage';
import { ThicknessDisplayMode, ThicknessViewState } from './helpers';
import { useThicknessComparison } from './useThicknessComparison';
import { useThicknessConfig } from './useThicknessConfig';
import { useThicknessReview } from './useThicknessReview';

interface UseCADThicknessPanelArgs {
  model: CADModel | null;
  requestedAnalysisId: string | null;
  requestedRegionId: string | null;
  refreshKey: number;
  seedAnalysis?: ThicknessAnalysis | null;
  urlWritePending?: boolean;
  onAnalysisChange: (analysisId: string | null) => void;
  onRegionChange: (regionId: string | null) => void;
  onViewChange: (state: ThicknessViewState) => void;
}

/**
 * Orchestrates every stateful concern of CADThicknessPanel: the analysis list
 * with polling, the displayed run detail, run start/report/recover actions, and
 * the composed config/review/comparison sub-hooks. The panel component consumes
 * the returned object purely to render.
 */
export const useCADThicknessPanel = ({
  model,
  requestedAnalysisId,
  requestedRegionId,
  refreshKey,
  seedAnalysis = null,
  urlWritePending = false,
  onAnalysisChange,
  onRegionChange,
  onViewChange,
}: UseCADThicknessPanelArgs) => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [analysisListError, setAnalysisListError] = useState<string | null>(null);
  // The requested-id effect must not judge a deep link before the list has come
  // back, or a cold load would flag every linked run as missing.
  const [analysisListLoaded, setAnalysisListLoaded] = useState(false);
  // The model the loaded list belongs to: on a model change the old list is still loaded for a render.
  const [listModelId, setListModelId] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [invalidRequestedAnalysisId, setInvalidRequestedAnalysisId] = useState<
    string | null
  >(null);
  const [regionLinkError, setRegionLinkError] = useState<string | null>(null);

  const config = useThicknessConfig({ t, setError });

  const [analyses, setAnalyses] = useState<ThicknessAnalysis[]>([]);
  const [latestSuccessfulId, setLatestSuccessfulId] = useState<string | null>(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);
  const [displayedAnalysis, setDisplayedAnalysis] = useState<ThicknessAnalysis | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<ThicknessRegion | null>(null);
  const [displayMode, setDisplayMode] = useState<ThicknessDisplayMode>('smooth');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [reporting, setReporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [recovering, setRecovering] = useState(false);
  const currentModelIdRef = useRef<string | null>(model?.id || null);
  const refreshGenerationRef = useRef(0);
  currentModelIdRef.current = model?.id || null;

  const review = useThicknessReview({ model, displayedAnalysis, t, setError });
  const comparison = useThicknessComparison({ model, displayedAnalysis, t, setError });

  const selectedRecord = useMemo(
    () => analyses.find((analysis) => analysis.analysisId === selectedAnalysisId) || null,
    [analyses, selectedAnalysisId],
  );
  const selectedRecordId = selectedRecord?.analysisId || null;
  const selectedRecordStatus = selectedRecord?.status || null;
  // A link to a non-existent run must show nothing rather than silently
  // substituting the latest successful analysis, which reads as that run's data.
  const displayCandidateId = invalidRequestedAnalysisId
    ? null
    : selectedRecordStatus === 'completed'
      ? selectedRecordId
      : latestSuccessfulId;
  const displayCandidateModelId = analyses.find(
    (analysis) => analysis.analysisId === displayCandidateId,
  )?.modelId || null;

  // A run the list does not confirm (list 403/404, or absent from a loaded list) must not stay on screen.
  const clearDisplayedRun = useCallback(() => {
    setSelectedAnalysisId(null);
    setDisplayedAnalysis(null);
    setDisplayMode('smooth');
  }, []);

  const refreshAnalyses = useCallback(async (preferId?: string | null) => {
    if (!model) return;
    const modelId = model.id;
    const generation = refreshGenerationRef.current + 1;
    refreshGenerationRef.current = generation;
    try {
      const response = await listThicknessAnalyses(modelId);
      if (
        currentModelIdRef.current !== modelId
        || refreshGenerationRef.current !== generation
      ) {
        return;
      }
      setAnalyses(response.analyses);
      setListModelId(modelId);
      setAnalysisListLoaded(true);
      setAnalysisListError(null);
      setRetryAfterSeconds(null);
      setLatestSuccessfulId(response.latestSuccessfulAnalysisId);
      if (
        requestedAnalysisId
        && !response.analyses.some(
          (item) => item.analysisId === requestedAnalysisId,
        )
      ) {
        setInvalidRequestedAnalysisId(requestedAnalysisId);
        setSelectedAnalysisId(null);
        setDisplayedAnalysis(null);
        setSelectedRegion(null);
        return;
      }
      setInvalidRequestedAnalysisId(null);
      const requested = requestedAnalysisId || preferId;
      const nextId =
        (requested && response.analyses.some((item) => item.analysisId === requested)
          ? requested
          : null) ||
        (selectedAnalysisId &&
        response.analyses.some((item) => item.analysisId === selectedAnalysisId)
          ? selectedAnalysisId
          : null) ||
        response.latestSuccessfulAnalysisId ||
        response.analyses[0]?.analysisId ||
        null;
      setSelectedAnalysisId(nextId);
      if (nextId !== requestedAnalysisId) onAnalysisChange(nextId);
    } catch (refreshError) {
      if (
        currentModelIdRef.current !== modelId
        || refreshGenerationRef.current !== generation
      ) {
        return;
      }
      const status = apiErrorStatus(refreshError);
      if (status === 403 || status === 404) {
        evictGlbsUnder(getModelGlbPrefix(modelId));
        clearDisplayedRun();
        setAnalyses([]);
        setAnalysisListLoaded(false);
        setLatestSuccessfulId(null);
        setInvalidRequestedAnalysisId(null);
      }
      setAnalysisListError(
        getRateLimitRetryAfterSeconds(refreshError)
          ? t('cad.thickness.rateLimited', {
            seconds: getRateLimitRetryAfterSeconds(refreshError),
          })
          : resolveApiErrorMessage(t, refreshError, {
            fallbackKey: 'cad.thickness.loadFailed',
          }),
      );
      setRetryAfterSeconds(getRateLimitRetryAfterSeconds(refreshError));
    }
  }, [
    clearDisplayedRun,
    model,
    onAnalysisChange,
    requestedAnalysisId,
    selectedAnalysisId,
    t,
  ]);

  useEffect(() => {
    let cancelled = false;
    setAnalyses([]);
    setLatestSuccessfulId(null);
    setSelectedAnalysisId(null);
    setDisplayedAnalysis(null);
    setSelectedRegion(null);
    setDisplayMode('smooth');
    setError(null);
    setAnalysisListError(null);
    setAnalysisListLoaded(false);
    setRetryAfterSeconds(null);
    setInvalidRequestedAnalysisId(null);
    setRegionLinkError(null);
    // Opening a finished run keeps it on screen instead of passing through the
    // base view, which would download the base GLB for nothing.
    const seeded = model && seedAnalysis?.modelId === model.id
      && seedAnalysis.status === 'completed' && seedAnalysis.result
      ? seedAnalysis
      : null;
    if (seeded) {
      setSelectedAnalysisId(seeded.analysisId);
      setDisplayedAnalysis(seeded);
      setDisplayMode('flat');
    }
    if (model?.status === 'completed') {
      setLoading(true);
      refreshAnalyses(seeded?.analysisId ?? requestedAnalysisId).finally(() => {
        if (!cancelled) setLoading(false);
      });
    }
    return () => {
      cancelled = true;
      refreshGenerationRef.current += 1;
    };
    // refreshKey intentionally triggers a list refresh after queued upload/analysis start.
  }, [model?.id, model?.status, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !model ||
      !analyses.some(
        (analysis) => analysis.status === 'pending' || analysis.status === 'processing',
      )
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      refreshAnalyses(selectedAnalysisId);
    }, THICKNESS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [analyses, model, refreshAnalyses, selectedAnalysisId]);

  useEffect(() => {
    if (!model || !displayCandidateId) return;
    // On a model change this render still holds the previous model's list; never request its run under the new model.
    if (displayCandidateModelId !== model.id) return;
    if (displayedAnalysis?.analysisId === displayCandidateId) return;
    let cancelled = false;
    setLoading(true);
    getThicknessAnalysis(model.id, displayCandidateId)
      .then((detail) => {
        if (cancelled) return;
        setDisplayedAnalysis(detail);
        setDisplayMode('flat');
      })
      .catch((detailError) => {
        if (!cancelled) {
          setError(
            resolveApiErrorMessage(t, detailError, {
              fallbackKey: 'cad.thickness.loadFailed',
            }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    displayCandidateId,
    displayCandidateModelId,
    displayedAnalysis?.analysisId,
    model,
    t,
  ]);

  // Whatever put a run on screen (a seed or a late detail response), a loaded list that lacks it wins.
  useEffect(() => {
    if (!analysisListLoaded || !displayedAnalysis) return;
    if (!analyses.some((analysis) => analysis.analysisId === displayedAnalysis.analysisId)) {
      clearDisplayedRun();
    }
  }, [analyses, analysisListLoaded, clearDisplayedRun, displayedAnalysis]);

  useEffect(() => {
    if (!selectedRecord) return;
    config.applyConfig(selectedRecord.config);
    // A polling refresh returns new record objects. Only a run selection change
    // should overwrite the operator's in-progress condition edits.
  }, [selectedRecordId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!analysisListLoaded || !requestedAnalysisId || listModelId !== model?.id) return;
    const exists = analyses.some(
      (analysis) => analysis.analysisId === requestedAnalysisId,
    );
    if (exists) {
      setInvalidRequestedAnalysisId(null);
      if (requestedAnalysisId !== selectedAnalysisId) {
        setSelectedAnalysisId(requestedAnalysisId);
      }
      return;
    }
    setInvalidRequestedAnalysisId(requestedAnalysisId);
    setSelectedAnalysisId(null);
    setDisplayedAnalysis(null);
    setSelectedRegion(null);
  }, [
    analyses,
    analysisListLoaded,
    listModelId,
    model?.id,
    requestedAnalysisId,
    selectedAnalysisId,
  ]);

  useEffect(() => {
    if (!displayedAnalysis?.result) {
      setSelectedRegion(null);
      setRegionLinkError(null);
      return;
    }
    // Keep the region the user just picked until our own URL write commits.
    if (urlWritePending) return;
    const region =
      displayedAnalysis.result.regions.find(
        (item) => item.regionId === requestedRegionId,
      ) || null;
    setSelectedRegion(region);
    setRegionLinkError(
      requestedRegionId && !region
        ? t('cad.thickness.regionNotFound', {
          regionId: requestedRegionId,
        })
        : null,
    );
  }, [displayedAnalysis, requestedRegionId, t, urlWritePending]);

  useEffect(() => {
    onViewChange({
      analysis: displayedAnalysis,
      region: selectedRegion,
      displayMode,
    });
  }, [displayMode, displayedAnalysis, onViewChange, selectedRegion]);

  const { lowerMm, upperMm, toleranceMm, clusterRadiusMm, clusterMinPoints, profile } = config;

  const startAnalysis = useCallback(async (retryOf?: string | null) => {
    if (!model) return;
    // Schema + safeParse, no react-hook-form: these values live in
    // useThicknessConfig and feed the tolerance preview and preset save too.
    // Field issues order before the cross-field one, so issues[0] keeps the
    // shipped invalid-number-over-range precedence.
    const conditions = thicknessConditionsSchema.safeParse({
      lowerMm,
      upperMm,
      toleranceMm,
    });
    if (!conditions.success) {
      setError(conditions.error.issues[0].message);
      return;
    }
    setStarting(true);
    // Invalidate a list request that started before this mutation. Otherwise
    // its stale response can remove the newly created run from local state.
    refreshGenerationRef.current += 1;
    try {
      const created = await createThicknessAnalysis(model.id, {
        lowerMm,
        upperMm,
        judgmentToleranceMm: toleranceMm,
        clusterRadiusMm,
        clusterMinPoints,
        profile,
        retryOf,
      });
      setAnalyses((current) => [
        created,
        ...current.filter((analysis) => analysis.analysisId !== created.analysisId),
      ]);
      setSelectedAnalysisId(created.analysisId);
      setSelectedRegion(null);
      onAnalysisChange(created.analysisId);
      setError(null);
    } catch (startError) {
      setError(
        resolveApiErrorMessage(t, startError, {
          fallbackKey: 'cad.thickness.startFailed',
        }),
      );
    } finally {
      setStarting(false);
    }
  }, [
    clusterMinPoints,
    clusterRadiusMm,
    lowerMm,
    model,
    onAnalysisChange,
    profile,
    t,
    toleranceMm,
    upperMm,
  ]);

  const handleReport = useCallback(async () => {
    if (!model || !displayedAnalysis) return;
    setReporting('xlsx');
    try {
      const baseName = model.filename.replace(/\.[^.]+$/, '');
      await downloadThicknessReport(
        model.id,
        displayedAnalysis.analysisId,
        `${baseName}_${t('cad.thickness.reportFileSlug')}_${displayedAnalysis.analysisId.slice(0, 8)}.xlsx`,
      );
    } catch (reportError) {
      setError(
        resolveApiErrorMessage(t, reportError, {
          fallbackKey: 'cad.thickness.reportFailed',
        }),
      );
    } finally {
      setReporting(null);
    }
  }, [displayedAnalysis, model, t]);

  const handlePdf = useCallback(async () => {
    if (!model || !displayedAnalysis) return;
    setReporting('pdf');
    try {
      const baseName = model.filename.replace(/\.[^.]+$/, '');
      await downloadThicknessPdf(
        model.id,
        displayedAnalysis.analysisId,
        `${baseName}_${t('cad.thickness.reportFileSlug')}_${displayedAnalysis.analysisId.slice(0, 8)}.pdf`,
      );
    } catch (reportError) {
      setError(
        resolveApiErrorMessage(t, reportError, {
          fallbackKey: 'cad.thickness.reportFailed',
        }),
      );
    } finally {
      setReporting(null);
    }
  }, [displayedAnalysis, model, t]);

  const selectedRecordIsStale = useMemo(() => {
    if (
      !selectedRecord
      || (selectedRecord.status !== 'pending' && selectedRecord.status !== 'processing')
    ) {
      return false;
    }
    const heartbeat = selectedRecord.heartbeatAt || selectedRecord.updatedAt;
    return Date.now() - new Date(heartbeat).getTime() >= THICKNESS_STALE_RUN_MS;
  }, [selectedRecord]);

  const handleRecover = useCallback(async () => {
    if (!model || !selectedRecord) return;
    setRecovering(true);
    // Recovery mutates the selected run; ignore an older in-flight poll.
    refreshGenerationRef.current += 1;
    try {
      const recovered = await recoverThicknessAnalysis(
        model.id,
        selectedRecord.analysisId,
      );
      setAnalyses((current) => current.map((item) =>
        item.analysisId === recovered.analysisId ? recovered : item
      ));
      setError(null);
    } catch (recoverError) {
      setError(
        resolveApiErrorMessage(t, recoverError, {
          fallbackKey: 'cad.thickness.recoverFailed',
        }),
      );
    } finally {
      setRecovering(false);
    }
  }, [model, selectedRecord, t]);

  const { reviewDirty } = review;
  const { setComparisonBaseId, setComparison } = comparison;

  const retryAnalysisList = useCallback(() => {
    setLoading(true);
    setAnalysisListError(null);
    setRetryAfterSeconds(null);
    refreshAnalyses(requestedAnalysisId).finally(() => setLoading(false));
  }, [refreshAnalyses, requestedAnalysisId]);

  const selectAnalysis = useCallback((nextId: string) => {
    if (
      reviewDirty
      && !window.confirm(t('confirmation.unsavedChanges'))
    ) {
      return;
    }
    // Picking a run by hand clears the deep-link complaints it supersedes.
    setInvalidRequestedAnalysisId(null);
    setRegionLinkError(null);
    setSelectedAnalysisId(nextId);
    setComparisonBaseId('');
    setComparison(null);
    setSelectedRegion(null);
    // onAnalysisChange also clears ?region= (see its prop doc).
    onAnalysisChange(nextId);
  }, [
    onAnalysisChange,
    reviewDirty,
    setComparison,
    setComparisonBaseId,
    t,
  ]);

  const clearRegionSelection = useCallback(() => {
    setSelectedRegion(null);
    onRegionChange(null);
  }, [onRegionChange]);

  const selectRegion = useCallback((region: ThicknessRegion) => {
    if (selectedRegion?.regionId === region.regionId) {
      clearRegionSelection();
      return;
    }
    setSelectedRegion(region);
    onRegionChange(region.regionId);
    setDisplayMode('flat');
  }, [clearRegionSelection, onRegionChange, selectedRegion?.regionId]);

  const result = displayedAnalysis?.result;
  const thinRegions = result?.regions.filter((region) => region.judgment === 'thin') || [];
  const thickRegions = result?.regions.filter((region) => region.judgment === 'thick') || [];
  const limitations = Array.from(
    new Set([
      ...(result?.qualityReasons || []),
      ...(displayedAnalysis?.evidence?.limitations || []),
    ]),
  );

  return {
    t,
    i18n,
    ...config,
    ...review,
    ...comparison,
    error,
    setError,
    analysisListError,
    analysisListLoaded,
    setAnalysisListError,
    retryAfterSeconds,
    invalidRequestedAnalysisId,
    regionLinkError,
    setRegionLinkError,
    retryAnalysisList,
    analyses,
    latestSuccessfulId,
    selectedAnalysisId,
    displayedAnalysis,
    selectedRegion,
    setSelectedRegion,
    displayMode,
    setDisplayMode,
    loading,
    starting,
    reporting,
    recovering,
    selectedRecord,
    selectedRecordIsStale,
    result,
    thinRegions,
    thickRegions,
    limitations,
    startAnalysis,
    handleReport,
    handlePdf,
    handleRecover,
    selectAnalysis,
    selectRegion,
    clearRegionSelection,
  };
};

export type ThicknessPanelController = ReturnType<typeof useCADThicknessPanel>;
